import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import cx from 'classnames'
import { StoreSnapshot, Editor, createTLStore } from '@tldraw/tldraw'

import Styles from './FullScreenPreviewWithCarouselV2.module.scss'
import { ChevronLeft, ChevronRight } from 'blaze-ui/icons/24'
import { PostPreview } from './PostPreview'
import { FullScreenPreviewProps, TYPES_WITH_PREVIEW } from './Types'
import { NavigationButton } from './NavigationButton'
import { Header } from './Header'
import { LoadingPreview } from './LoadingPreview/LoadingPreview'
import { carouselClasses } from './carouselClasses'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useIsScheduledPostsV3 } from 'hooks/isScheduledPostsV3'
import { Chat } from 'components/Chat'
import { useMarkFileAsViewed } from '../useMarkFileAsViewed'
import { Toolbar } from './Toolbar/Toolbar'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { MediaObject } from 'types'
import { shapeUtils } from 'components/VisualStudioPlayground/VisualEditor/VisualEditor'
import { isBlankDesign as checkIsBlankDesign } from 'components/Design/utils/isBlankDesign'
import { DesignChannel } from 'components/NewFileModal/Types'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { useContentPreviewFiles } from 'hooks/calendar/useContentPreviewFiles'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { ViewAsMenu } from './ViewAsMenu/ViewAsMenu'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { useFullscreenBackNavigation } from './useFullscreenBackNavigation'
import { useOpenFullscreenPost } from '../WeekViewV2/useOpenLastFullscreenPost'

type Direction = 'left' | 'right'

export const CROSSPOST_UNSUPPORTED_CHANNELS: string[] = [ChannelType.Newsletter, ChannelType.BlogPost]

// Pre-allocated style objects to avoid recreation on every render
const VISIBLE_SLIDE_STYLE = { zIndex: 1, opacity: 1, pointerEvents: 'auto' as const }
const SENTINEL_STYLE = { top: 20, left: 0, height: 1, width: '100%', position: 'absolute' as const }

export const FullScreenPreviewWithCarouselV2 = ({
  id,
  startDate,
  filters,
  onClose,
  channel,
  width,
  height,
  assetType,
  loadingStateColor,
  showCarousel = true,
  useChat = true,
}: FullScreenPreviewProps) => {
  const { markFileAsViewed } = useMarkFileAsViewed()

  const {
    currentItem,
    displayedFile,
    handleRemoveCurrentFile,
    handleUpdateAlmanacFile,
    isFileFetching,
    isFileLoading,
    nextFile,
    nextKey,
    previousFile,
    previousKey,
    setCurrentItem,
  } = useContentPreviewFiles({
    id,
    startDate,
    filters,
    showCarousel,
    onClose,
  })

  const fileKey = displayedFile?.key || currentItem.id || id
  const filePostAt = displayedFile?.post_at || currentItem.startDate || startDate || ''

  useOpenFullscreenPost({
    fileKey,
    filePostAt,
    fileChannel: displayedFile?.channel,
  })
  const { useBackButtonListener, handleClose, handleBack } = useFullscreenBackNavigation({
    onClose,
    currentItemDate: displayedFile?.post_at || currentItem.startDate,
  })

  useBackButtonListener()

  const [isHeaderIntersecting, setIsHeaderIntersecting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [platformsByFile, setPlatformsByFile] = useState<Record<string, ExternalIntegrationEnum | undefined>>({})
  const [isInsertingMediaObjects, setIsInsertingMediaObjects] = useState(false)
  const [seeMore, setSeeMore] = useState(false)

  const designerChatEnabled = useFlipperFlag('designer_chat_assistant')
  const scheduledPostTargetsEnabled = useIsScheduledPostsV3()
  const postApprovalsEnabled = useFlipperFlag('post_approvals')

  useEffect(() => {
    markFileAsViewed(currentItem.id)
  }, [currentItem.id, markFileAsViewed])

  const selectPlatformHandlersRef = useRef<Record<string, (platform: ExternalIntegrationEnum) => void>>({})
  const platformsByFileRef = useRef<Record<string, ExternalIntegrationEnum | undefined>>({})

  const previewRef = useRef<HTMLDivElement>(null)
  const postPreviewRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const centerColumnRef = useRef<HTMLDivElement>(null)
  const topSentinelRef = useRef<HTMLDivElement>(null)

  const [previewSize, setPreviewSize] = useState({ width: 0, height: 0 })

  useLayoutEffect(() => {
    const node = previewRef.current
    if (!node) return

    const measure = () => {
      const rect = node.getBoundingClientRect()
      setPreviewSize({ width: rect.width, height: rect.height })
    }

    measure()

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => measure())
      observer.observe(node)
      return () => observer.disconnect()
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', measure)
      return () => window.removeEventListener('resize', measure)
    }

    return undefined
  }, [displayedFile?.key])

  const elementWidth = previewSize.width
  const elementHeight = previewSize.height

  const showPostPreview = displayedFile?.channel && TYPES_WITH_PREVIEW.includes(displayedFile.channel)

  const currentItemIndex = useMemo(() => (previousFile ? 1 : 0), [previousFile])

  // Get classes for the current (center) item only
  const currentItemClasses = useMemo(() => {
    if (!displayedFile) return {}
    // Use displayedFile as fallback for next/previous if they're null (only affects non-current items)
    return carouselClasses(
      displayedFile,
      nextFile || displayedFile,
      previousFile || displayedFile,
      showCarousel,
      currentItemIndex,
      elementHeight,
      elementWidth,
      !!(scheduledPostTargetsEnabled && showPostPreview),
      true,
    ).getCarouselItemClassNames(currentItemIndex)
  }, [
    displayedFile,
    nextFile,
    previousFile,
    currentItemIndex,
    elementHeight,
    elementWidth,
    scheduledPostTargetsEnabled,
    showPostPreview,
    showCarousel,
  ])

  const startNavigation = useCallback(
    (direction: Direction) => {
      // Force synchronous update - bypasses React 18's automatic batching
      flushSync(() => {
        if (direction === 'right' && nextKey) {
          setCurrentItem({ id: nextKey, startDate: nextFile?.post_at ?? null })
        } else if (direction === 'left' && previousKey) {
          setCurrentItem({ id: previousKey, startDate: previousFile?.post_at ?? null })
        }
      })
    },
    [nextKey, nextFile?.post_at, previousKey, previousFile?.post_at, setCurrentItem],
  )

  const handleLeftClick = useCallback(() => startNavigation('left'), [startNavigation])
  const handleRightClick = useCallback(() => startNavigation('right'), [startNavigation])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      if (
        target.closest('[role="dialog"]') ||
        target.closest('textarea') ||
        target.closest('input') ||
        target.closest('[contenteditable="true"]')
      ) {
        return
      }

      if (e.key === 'Escape') handleBack()
      else if (e.key === 'ArrowLeft') startNavigation('left')
      else if (e.key === 'ArrowRight') startNavigation('right')
    },
    [handleBack, startNavigation],
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  useLayoutEffect(() => {
    const rootEl = centerColumnRef.current
    const targetEl = topSentinelRef.current
    if (!rootEl || !targetEl) return

    setIsHeaderIntersecting(rootEl.scrollTop > 0)

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        setIsHeaderIntersecting(!entry.isIntersecting)
      },
      {
        root: rootEl,
        rootMargin: '0px 0px 0px 0px',
        threshold: 0,
      },
    )

    observer.observe(targetEl)

    return () => {
      observer.disconnect()
    }
  }, [displayedFile?.key])

  // Check if design is blank - create editor, check, then dispose immediately to free memory
  const isDesignBlank = useMemo(() => {
    if (!displayedFile?.design_content) return false
    const store = createTLStore({ shapeUtils })
    store.loadSnapshot(displayedFile.design_content as StoreSnapshot<any>)
    const editor = new Editor({
      store,
      shapeUtils: shapeUtils as any,
      tools: [],
      getContainer: () => document.createElement('div'),
    })
    const isBlank = checkIsBlankDesign(editor)
    // Dispose the editor immediately to prevent memory leaks
    editor.dispose()
    return isBlank
  }, [displayedFile?.design_content])

  // Sync state to ref for stable callback
  platformsByFileRef.current = platformsByFile

  const getCurrentPreviewPlatform = useCallback((fileKey: string) => platformsByFileRef.current[fileKey], [])
  const setPreviewPlatformForFile = useCallback((fileKey: string, platform: ExternalIntegrationEnum) => {
    setPlatformsByFile((prev) => {
      if (prev[fileKey] === platform) return prev
      return { ...prev, [fileKey]: platform }
    })
  }, [])

  const getSelectPlatformHandler = useCallback(
    (fileKey: string) => {
      if (!selectPlatformHandlersRef.current[fileKey]) {
        selectPlatformHandlersRef.current[fileKey] = (platform: ExternalIntegrationEnum) =>
          setPreviewPlatformForFile(fileKey, platform)
      }
      return selectPlatformHandlersRef.current[fileKey]
    },
    [setPreviewPlatformForFile],
  )

  const toolbarPostWithMediaObjects = useMemo(() => {
    if (!displayedFile) return undefined
    if (displayedFile?.scheduled_post?.media_objects.length === 0 && displayedFile?.image_url) {
      return {
        ...displayedFile?.scheduled_post,
        media_objects: [
          {
            id: 1,
            source_url: displayedFile?.image_url,
            thumbnail_url: displayedFile?.image_url,
            mime_type: 'image/png',
            metadata: { width: displayedFile?.width, height: displayedFile?.height },
            design_key: displayedFile?.key,
            position: 0,
          } as MediaObject,
        ],
      } as ScheduledPostShow
    }
    return displayedFile?.scheduled_post
  }, [displayedFile])

  const shouldShowFileLoadingState = !displayedFile && (isFileLoading || isFileFetching)
  const showLeftNavigation = (previousKey || shouldShowFileLoadingState) && showCarousel
  const showRightNavigation = (nextKey || shouldShowFileLoadingState) && showCarousel

  // Create stable delete handler that doesn't change
  const getDeleteHandler = useCallback(
    (fileKey: string) => () => handleRemoveCurrentFile(fileKey),
    [handleRemoveCurrentFile],
  )

  const isCurrentChannelUnsupported = CROSSPOST_UNSUPPORTED_CHANNELS.includes(displayedFile?.channel as string)

  // Render the current slide only
  const currentFilePreviewPlatform = displayedFile?.key ? getCurrentPreviewPlatform(displayedFile.key) : undefined

  const renderSlide = useCallback(
    (file: AlmanacFileShowItem) => {
      const deleteHandler = getDeleteHandler(file.key)
      const viewAsMenuEnabled = postApprovalsEnabled && channel && !isCurrentChannelUnsupported

      return (
        <div key={file.key} className={Styles.slide} ref={previewRef} data-role="current" style={VISIBLE_SLIDE_STYLE}>
          <div className={cx(Styles.carouselItem, currentItemClasses, { [Styles.overflowing]: !!seeMore })}>
            <div id="preview-sentinel" ref={topSentinelRef} style={SENTINEL_STYLE} />
            <ConditionalWrapper
              condition={viewAsMenuEnabled}
              wrapper={(children) => (
                <div className={cx({ [Styles.previewContentWrapper]: viewAsMenuEnabled })}>
                  <ViewAsMenu
                    activePlatform={currentFilePreviewPlatform}
                    onSelectPlatform={(platform: ExternalIntegrationEnum) => {
                      const platformHandler = getSelectPlatformHandler(file.key)
                      platformHandler(platform)
                      setSeeMore(false)
                    }}
                    fileChannel={(displayedFile?.channel || channel) as ChannelType | DesignChannel | GenerationType}
                  />
                  {children}
                </div>
              )}
            >
              <PostPreview
                ref={postPreviewRef}
                almanacFile={file}
                isLoading={shouldShowFileLoadingState}
                showVideoControls
                isActive={true}
                setIsLoading={setIsLoading}
                onClose={handleClose}
                onDelete={deleteHandler}
                filters={filters}
                fileChannel={channel}
                startDate={startDate || undefined}
                previewPlatform={currentFilePreviewPlatform}
                onSelectPlatform={getSelectPlatformHandler(file.key)}
                isInsertingMedia={isInsertingMediaObjects}
                isTransitioning={false}
                showHoverActionOverlay
                onSeeMoreToggle={(expanded: boolean) => setSeeMore(expanded)}
                seeMore={seeMore}
              />
            </ConditionalWrapper>
          </div>
        </div>
      )
    },
    [
      getDeleteHandler,
      postApprovalsEnabled,
      channel,
      isCurrentChannelUnsupported,
      currentItemClasses,
      seeMore,
      shouldShowFileLoadingState,
      setIsLoading,
      handleClose,
      filters,
      startDate,
      currentFilePreviewPlatform,
      getSelectPlatformHandler,
      isInsertingMediaObjects,
      displayedFile?.channel,
    ],
  )

  if (shouldShowFileLoadingState)
    return (
      <div className={Styles.fullScreenPreview}>
        <div className={Styles.loading} data-testid="loading-preview">
          <LoadingPreview
            channel={channel}
            onClose={handleBack}
            width={width}
            height={height}
            assetType={assetType}
            color={loadingStateColor}
          />
        </div>
      </div>
    )

  if (!displayedFile)
    return (
      <div className={Styles.fullScreenPreview}>
        <div className={Styles.loading} data-testid="loading-preview">
          <LoadingPreview
            channel={channel}
            onClose={handleBack}
            width={width}
            height={height}
            assetType={assetType}
            color={loadingStateColor}
          />
        </div>
      </div>
    )

  const showChat = designerChatEnabled && useChat && displayedFile?.asset_type === 'Design'

  return (
    <div className={Styles.fullScreenPreview}>
      <Header
        id={currentItem.id}
        startDate={currentItem.startDate}
        channel={channel ?? undefined}
        almanacFile={displayedFile || undefined}
        isIntersecting={isHeaderIntersecting}
        onClose={handleBack}
        ref={headerRef}
        filters={filters}
        isLoading={isLoading || shouldShowFileLoadingState}
        setIsLoading={setIsLoading}
        onDelete={() => handleRemoveCurrentFile(displayedFile?.key)}
        chatOpen={showChat}
      />

      <div className={Styles.body} data-chat-open={showChat}>
        <div className={Styles.leftColumn}>
          <div
            className={cx(Styles.chatContainer, { [Styles.isVisible]: showChat })}
            data-chat-animation="idle"
            data-current-visible={showChat}
            data-show-chat={showChat}
          >
            {showChat && displayedFile?.key && (
              <Chat isOpen fileId={displayedFile.key} context={{ design_id: displayedFile.key }} />
            )}
          </div>
        </div>

        <div className={Styles.centerColumn} ref={centerColumnRef}>
          {showLeftNavigation && !postApprovalsEnabled && (
            <NavigationButton
              side="left"
              icon={ChevronLeft}
              isDisabled={!previousKey}
              onPress={handleLeftClick}
              chatOpen={showChat}
            />
          )}

          <div
            className={cx(Styles.carousel, {
              [Styles.isArticlePreview]: displayedFile?.asset_type === 'Article',
            })}
            data-phase="idle"
            data-direction="right"
            role="region"
            aria-roledescription="carousel"
          >
            {renderSlide(displayedFile)}
          </div>

          {showRightNavigation && !postApprovalsEnabled && (
            <NavigationButton
              side="right"
              icon={ChevronRight}
              isDisabled={!nextKey}
              onPress={handleRightClick}
              chatOpen={showChat}
            />
          )}
        </div>

        <div className={Styles.rightColumn}>
          {displayedFile && channel && (
            <Toolbar
              scheduledPost={toolbarPostWithMediaObjects as ScheduledPostShow}
              almanacFile={displayedFile}
              previewPlatform={getCurrentPreviewPlatform(displayedFile.key)}
              onSelectPlatform={getSelectPlatformHandler(displayedFile.key)}
              isBlankDesign={isDesignBlank}
              onLoadingChange={setIsInsertingMediaObjects}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              onClose={handleClose}
              onDelete={(skipNavigation) => handleRemoveCurrentFile(displayedFile.key, skipNavigation)}
              filters={filters}
              fileChannel={channel}
              startDate={startDate || undefined}
              animationState="idle"
              onUpdateAlmanacFile={handleUpdateAlmanacFile}
              isCurrentChannelUnsupported={isCurrentChannelUnsupported}
              showNavigation={postApprovalsEnabled && showCarousel}
              goToPreviousFile={previousFile ? handleLeftClick : undefined}
              goToNextFile={nextFile ? handleRightClick : undefined}
            />
          )}
        </div>
      </div>
    </div>
  )
}
